/**
 * SendAbhaLinkSms — staff button that texts the ABDM-app link to a patient's mobile
 * (owner R20: button only; the automatic SMS on visit completion was removed).
 * Visible to ADMIN, DOCTOR and RECEPTIONIST (owner R30). Confirms through
 * ConfirmationModal, then sends once (single-flight).
 */
import React, { useRef, useState } from 'react';

import ConfirmationModal from '../common/ConfirmationModal/ConfirmationModal';
import { usePermission } from '../../context/PermissionContext';
import { useTheme } from '../../theme/ThemeContext';
import { sendDeepLinkSms } from '../../services/api/hipApi';

const MOBILE_RE = /^[6-9]\d{9}$/;
const NO_MOBILE_REASON = 'Enter the patient’s 10-digit mobile number to send the link.';

/** @param {{ phone?: string }} props optional prefill (the patient's mobile on file) */
export default function SendAbhaLinkSms({ phone = '' }) {
  const { isDark } = useTheme();
  const canSend = usePermission('HIP_LINK_SMS');
  // null until the user types; then their value wins over the prop.
  const [typed, setTyped] = useState(null);
  const mobile = typed ?? phone;
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { ok, text }
  const inFlight = useRef(false);

  if (!canSend) return null;

  const valid = MOBILE_RE.test(mobile);
  const masked = `******${mobile.slice(-4)}`;

  const send = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setConfirming(false);
    setSending(true);
    setResult(null);
    try {
      await sendDeepLinkSms(mobile);
      setResult({ ok: true, text: `Link sent to ${masked}.` });
    } catch (err) {
      setResult({ ok: false, text: err.userMessage || 'Could not send the link. Please try again.' });
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  };

  const border = `1px solid ${isDark ? 'rgba(255,255,255,0.25)' : '#9ca3af'}`;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 16 }}>
      <input
        type="tel"
        inputMode="numeric"
        maxLength={10}
        aria-label="Patient mobile number"
        placeholder="Patient mobile (10 digits)"
        value={mobile}
        onChange={(e) => { setTyped(e.target.value.replace(/\D/g, '')); setResult(null); }}
        style={{ background: isDark ? '#1e293b' : '#ffffff', color: isDark ? '#f1f5f9' : '#111827', border, borderRadius: 6, padding: '6px 8px', fontSize: 13 }}
      />
      <button
        onClick={() => setConfirming(true)}
        disabled={!valid || sending}
        title={valid ? undefined : NO_MOBILE_REASON}
        style={{ background: isDark ? '#1e293b' : '#f1f5f9', color: isDark ? '#f1f5f9' : '#1f2937', border, borderRadius: 6, padding: '6px 14px', fontSize: 13, cursor: valid && !sending ? 'pointer' : 'default', opacity: valid && !sending ? 1 : 0.6 }}
      >
        {sending ? 'Sending…' : 'Send ABHA app link (SMS)'}
      </button>
      {!valid && <span style={{ fontSize: 12, color: isDark ? '#9ca3af' : '#4b5563' }}>{NO_MOBILE_REASON}</span>}
      {result && (
        <span role="status" style={{ fontSize: 13, color: result.ok ? (isDark ? '#4ade80' : '#15803d') : (isDark ? '#f87171' : '#dc2626') }}>
          {result.text}
        </span>
      )}
      <ConfirmationModal
        isOpen={confirming}
        icon="📱"
        title="Send ABHA app link?"
        message={`We will text the ABHA app link to ${masked}.`}
        ctaLabel="Send link"
        onConfirm={send}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
